"use client";

import { useEffect } from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Loader, MapPin, Phone, Mail } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function ContactFormSection() {
  const {
    ref: sectionRef,
    isVisible: sectionVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const submitText = "Verstuur bericht";
  const successMessage = "Bedankt voor uw bericht! We nemen zo snel mogelijk contact met u op.";
  return <section ref={sectionRef} className={`py-20 bg-light-background transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${sectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">
          {}
          <FormProvider className="space-y-6" formId="contact-form-contactformulier" sectionName="contactformulier" defaultValues={{
          name: "",
          email: "",
          phone: "",
          message: ""
        }} validationRules={{
          name: {
            required: true,
            minLength: 2,
            maxLength: 50
          },
          email: {
            required: true,
            pattern: "email"
          },
          phone: {
            required: false,
            pattern: "phone"
          },
          message: {
            required: true,
            minLength: 10
          }
        }} submitText={submitText} successMessage={successMessage} errorMessage="Er is iets misgegaan. Probeer het opnieuw.">
              <ContactFormFields submitText={submitText} successMessage={successMessage} />
            </FormProvider>

          {}
          <div className="space-y-6">
            <ContactInfoItem icon={<MapPin className="size-6" />} title="Adres" content="Industriepark West 75 Sint Niklaas" delay={0} />
            <ContactInfoItem icon={<Phone className="size-6" />} title="Telefoon" content="0487412206" delay={100} />
            <ContactInfoItem icon={<Mail className="size-6" />} title="E-mail" content="info@ekonorm.be" delay={200} />
            
            {}
            <WorkingAreaInfo delay={300} />
          </div>
        </div>
      </div>
    </section>;
}
function ContactFormFields({
  submitText,
  successMessage: successMessageProp
}: {
  submitText: string;
  successMessage: string;
}) {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  useEffect(() => {
    if (isSubmitted && !submitError) {
      resetForm();
    }
  }, [isSubmitted, submitError, resetForm]);
  const {
    ref: field1Ref,
    isVisible: field1Visible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: field2Ref,
    isVisible: field2Visible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: field3Ref,
    isVisible: field3Visible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: field4Ref,
    isVisible: field4Visible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: buttonRef,
    isVisible: buttonVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  if (isSubmitted && !submitError) {
    return <div className="p-6 bg-primary-light border border-primary rounded-md shadow-soft">
        <p className="text-text-dark font-medium">{successMessageProp}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4">
          Nieuw bericht verzenden
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div ref={field1Ref} className={`transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${field1Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
        <FormField control={control} name="name" rules={{
        required: "Naam is verplicht",
        minLength: {
          value: 2,
          message: "Minimaal 2 karakters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Naam</FormLabel>
              <FormControl>
                <Input placeholder="Uw naam" {...field} className="rounded-md border border-border-light focus-visible:shadow-medium" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div ref={field2Ref} className={`transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] delay-100 ${field2Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
        <FormField control={control} name="email" rules={{
        required: "E-mail is verplicht",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Ongeldig e-mailadres"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>E-mail</FormLabel>
              <FormControl>
                <Input type="email" placeholder="uw@email.be" {...field} className="rounded-md border border-border-light focus-visible:shadow-medium" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div ref={field3Ref} className={`transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] delay-200 ${field3Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
        <FormField control={control} name="phone" rules={{
        pattern: {
          value: /^[\d\s+()-]+$/,
          message: "Ongeldig telefoonnummer"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Telefoon (optioneel)</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+32 2 123 45 67" {...field} className="rounded-md border border-border-light focus-visible:shadow-medium" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div ref={field4Ref} className={`transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] delay-300 ${field4Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
        <FormField control={control} name="message" rules={{
        required: "Bericht is verplicht",
        minLength: {
          value: 10,
          message: "Minimaal 10 karakters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Adres en type keuring</FormLabel>
              <FormControl>
                <Textarea placeholder="Uw bericht..." {...field} className="rounded-md border border-border-light focus-visible:shadow-medium min-h-32" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <div className="p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-800 text-sm">{submitError}</p>
        </div>}

      <div ref={buttonRef} className={`transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] delay-400 ${buttonVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
        <Button type="submit" disabled={isSubmitting} className="w-full bg-primary text-foreground-primary hover:bg-primary-hover hover:shadow-medium hover:scale-[1.04] active:bg-primary-active transition-all duration-[var(--duration-fast)] ease-[var(--ease-spring)]">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Versturen...
            </> : submitText}
        </Button>
      </div>
    </Form>;
}
function ContactInfoItem({
  icon,
  title,
  content,
  delay
}: {
  icon: React.ReactNode;
  title: string;
  content: string;
  delay: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  return <div ref={ref} className={`flex items-start gap-x-4 transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)]`} style={{
    transitionDelay: `${delay}ms`,
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? "translateY(0)" : "translateY(1rem)"
  }}>
      <div className="flex-shrink-0 size-12 rounded-full bg-primary-light flex items-center justify-center text-primary">
        {icon}
      </div>
      <div>
        <h3 className="text-lg font-semibold text-text-dark mb-1">{title}</h3>
        <p className="text-base text-text-dark/80">{content}</p>
      </div>
    </div>;
}
function WorkingAreaInfo({
  delay
}: {
  delay: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  return <div ref={ref} className={`pt-6 border-t border-border-light transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)]`} style={{
    transitionDelay: `${delay}ms`,
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? "translateY(0)" : "translateY(1rem)"
  }}>
      <h3 className="text-lg font-semibold text-text-dark mb-3">Werkgebied</h3>
      <p className="text-base text-text-dark/80 leading-relaxed">
        Wij bedienen alle gemeentes in de regio Kempen en Antwerpen, waaronder Antwerpen, Mechelen, Turnhout, Geel, Mol, Herentals, Lier, Hoogstraten, Westerlo, Balen, Heist-op-den-Berg, Olen, Grobbendonk, Vorselaar, Laakdal, Arendonk, Kasterlee, Ravels, Meerhout, Retie, Dessel, en omliggende gemeentes.
      </p>
    </div>;
}
export default function Contactformulier() {
  return <ContactFormSection />;
}